// #region IMPORTS
import ReticleMaterial from '@cartwhl/3d/threejs/materials/ReticleMaterial';
import RoundedSceneStencilMaterial from '@cartwhl/3d/threejs/materials/RoundedSceneStencilMaterial';
import screenQuad from '@cartwhl/3d/threejs/shapes/screenQuad';
import screenStencilQuad from '@cartwhl/3d/threejs/shapes/screenStencilQuad';
import {
  ViewPlacement,
  ViewportRenderer,
} from '@cartwhl/3d/threejs/utils/ViewportRenderer';
import { useFrame, useThree } from '@react-three/fiber';
import { useEffect, useRef } from 'react';
import { Color, Vector2 } from 'three';

// #endregion

// #region CONSTANTS

const EDGE_PAD = 8; // Distance from edges viewport shoult be

// #endregion

export default function ZoomScopeView() {
  const { scene, size, gl } = useThree();

  const positionRef = useRef(new Vector2());

  const stencilRef = useRef(
    screenStencilQuad(
      new RoundedSceneStencilMaterial({ padding: 0, radius: 10 }),
    ),
  );

  const reticleRef = useRef(
    screenQuad(
      new ReticleMaterial({
        hMin: 0,
        hMax: 1,
        vMin: 0,
        vMax: 1,
        useBox: false,
        vPad: 0.012,
        hPad: 0.012,
        rColor: new Color('#d0d0d0'),
        bColor: new Color('#9cc7d9'),
        padding: 3,
        radius: 7,
      }),
    ),
  );

  const viewportRef = useRef(
    new ViewportRenderer({
      width: 130,
      height: 80,
      padding: EDGE_PAD,
      zoom: 7,
      placement: ViewPlacement.TopRight,
    }),
  );

  // #region HOOKS

  // After Main Render
  useFrame(({ gl, scene, camera }) => {
    if (viewportRef.current !== undefined) {
      // ~~~~~~~~~~~~~~~~~~~~~~~~~~
      // Prepare for rendering
      reticleRef.current.visible = true;
      stencilRef.current.visible = true;

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~
      // For orthographic project, pass ndc mouse coordinates
      viewportRef.current
        .setPointer(positionRef.current)
        .render(scene, camera, gl);

      // ~~~~~~~~~~~~~~~~~~~~~~~~~~
      // Cleanup after rendering
      reticleRef.current.visible = false;
      stencilRef.current.visible = false;

      // Clear and disable stencil test
      gl.clearStencil();
      const ctx = gl.getContext();
      ctx.disable(ctx.STENCIL_TEST);
    }
  }, 100);

  // Manage extra meshes
  useEffect(() => {
    // Create visual elements
    const reticle = reticleRef.current;
    reticle.visible = false;
    reticle.name = 'reticle_quad';
    scene.add(reticle);

    const stencil = stencilRef.current;
    stencil.name = 'stencil_quad';
    stencil.visible = false;
    scene.add(stencil);

    // Track the mouse movements in relation to canvas
    const onMove = (e: PointerEvent) => {
      const rect = gl.domElement.getBoundingClientRect();
      positionRef.current.x = e.clientX - rect.left;
      positionRef.current.y = e.clientY - rect.top;
    };
    gl.domElement.addEventListener('pointermove', onMove);

    // Cleanup
    return () => {
      scene.remove(reticle);
      scene.remove(stencil);

      reticle.geometry.dispose();
      stencil.geometry.dispose();

      gl.domElement.removeEventListener('pointermove', onMove);
    };
  }, [scene, gl]);

  // Manage changes to canvas size
  useEffect(() => {
    const viewport = viewportRef.current;
    if (viewport) {
      // Uodate canvas size to recompute placement
      viewport.setCanvasSize(size.width, size.height);

      const viewSize = viewport.getSize();

      // Update canvas size for reticle canvas
      (reticleRef.current.material as ReticleMaterial).setScreenSize(
        viewSize[0],
        viewSize[1],
      );

      (
        stencilRef.current.material as RoundedSceneStencilMaterial
      ).setScreenSize(viewSize[0], viewSize[1]);
    }
  }, [size]);

  // #endregion

  return null;
}
